
import { useState, useCallback,useEffect,useRef } from "react";
import { useNavigate } from "react-router-dom";
import { SectionCard, formatDate, formatINR } from "../../UI/index";
import {useUpdateCaseInfo,useUpdateCaseStatus, useHandleProforma, useHandleAssessment,useHandleTaxInvoice} from "../../../../hooks/useWorkshopQueries";

function daysSince(dateStr) {
  if (!dateStr) return null;
  return Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
}

// ─── Countdown timer component (reusable) ────────────────────────────────────
function CountdownTimer({ startDate, limitHours, label }) {
  const elapsedMs    = Date.now() - new Date(startDate).getTime();
  const elapsedHours = Math.floor(elapsedMs / 3600000);
  const elapsedDays  = Math.floor(elapsedHours / 24);
  const elapsedHrsRem = elapsedHours % 24;
  const pct          = Math.min((elapsedHours / limitHours) * 100, 100);
  const overdue      = elapsedHours >= limitHours;
  const remaining    = Math.max(0, limitHours - elapsedHours);
  const remainDays   = Math.floor(remaining / 24);
  const remainHrs    = remaining % 24;

  const barColor = pct < 50 ? "var(--wd-green)" 
                 : pct < 80 ? "var(--wd-yellow)" 
                 : "var(--wd-red)";

  return (
    <div style={{
      marginTop: 10, padding: "10px 12px", borderRadius: 10,
      background: overdue ? "rgba(239,68,68,.06)" : "rgba(59,130,246,.05)",
      border: `1px solid ${overdue 
        ? "rgba(239,68,68,.2)" : "rgba(59,130,246,.15)"}`,
    }}>
      <div style={{ 
        display: "flex", alignItems: "center", 
        justifyContent: "space-between", marginBottom: 8 
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 13 }}>{overdue ? "⚠️" : "⏱️"}</span>
          <span style={{ 
            fontSize: 12, fontWeight: 600, 
            color: overdue ? "var(--wd-red)" : "var(--wd-t1)" 
          }}>
            {label}
          </span>
        </div>
        <div style={{ textAlign: "right" }}>
          <span style={{
            fontSize: 13, fontWeight: 700, fontFamily: "var(--wd-mono)",
            color: overdue ? "var(--wd-red)" : "var(--wd-t1)",
          }}>
            {overdue 
              ? `${elapsedDays}d ${elapsedHrsRem}h elapsed`
              : `${remainDays}d ${remainHrs}h left`
            }
          </span>
          <span style={{ fontSize: 11, color: "var(--wd-t3)", marginLeft: 4 }}>
            / {limitHours / 24}d limit
          </span>
        </div>
      </div>
      <div style={{ 
        height: 6, borderRadius: 3, 
        background: "var(--wd-hover)", overflow: "hidden" 
      }}>
        <div style={{
          height: "100%", borderRadius: 3,
          width: `${pct}%`, background: barColor,
          transition: "width .3s ease",
        }} />
      </div>
      <div style={{ 
        display: "flex", justifyContent: "space-between",
        marginTop: 5, fontSize: 10, color: "var(--wd-t3)",
      }}>
        <span>
          Started {new Date(startDate).toLocaleDateString("en-IN", 
            { day: "2-digit", month: "short" })}
        </span>
        <span style={{ 
          color: overdue ? "var(--wd-red)" : "var(--wd-t3)",
          fontWeight: overdue ? 600 : 400 
        }}>
          {overdue 
            ? `${elapsedHours - limitHours}h overdue` 
            : `Due ${new Date(new Date(startDate).getTime() + limitHours * 3600000)
                .toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}`
          }
        </span>
      </div>
    </div>
  );
}


export function StepLabel({ children }) {
  return (
    <div style={{
      fontSize: 10, fontWeight: 700, textTransform: "uppercase",
      letterSpacing: ".06em", color: "var(--wd-t3)", marginBottom: 4,
    }}>
      {children}
    </div>
  );
}
// A step block — coloured left border based on state
export function StepCard({ state = "pending", children }) {
  const colors = {
    done:    { border: "var(--wd-green)",              bg: "rgba(34,197,94,.05)"  },
    active:  { border: "var(--wd-blue)",               bg: "rgba(59,130,246,.05)" },
    warning: { border: "var(--wd-yellow)",             bg: "rgba(234,179,8,.05)"  },
    pending: { border: "var(--wd-border)",             bg: "var(--wd-card)"       },
    info:    { border: "var(--wd-purple, #a855f7)",    bg: "rgba(168,85,247,.05)" },
    skipped: { border: "var(--wd-purple, #a855f7)",    bg: "rgba(168,85,247,.04)" },
  };
  const c = colors[state] || colors.pending;
  return (
    <div style={{
      borderRadius: 12, overflow: "hidden",
      border: `1px solid ${c.border}44`,
      borderLeft: `3px solid ${c.border}`,
      background: c.bg,
      padding: "14px 16px",
    }}>
      {children}
    </div>
  );
}

export function StepHeader({ icon, title, subtitle, badge, badgeColor = "var(--wd-t3)" }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 16 }}>{icon}</span>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: "var(--wd-t1)" }}>{title}</div>
          {subtitle && <div style={{ fontSize: 11, color: "var(--wd-t3)", marginTop: 1 }}>{subtitle}</div>}
        </div>
      </div>
      {badge && (
        <span style={{
          fontSize: 10, fontWeight: 700, padding: "2px 9px", borderRadius: 20, flexShrink: 0,
          background: `${badgeColor}18`, color: badgeColor, border: `1px solid ${badgeColor}33`,
          textTransform: "uppercase", letterSpacing: ".04em",
        }}>
          {badge}
        </span>
      )}
    </div>
  );
}

function MailButton({ label, onClick, disabled }) {
  return (
    <button
      className="wd-btn wd-btn-ghost wd-btn-sm"
      style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12 }}
      onClick={onClick}
      disabled={disabled}
    >
      <span>📧</span> {label}
    </button>
  );
}
 
function SaveRow({ onSave, onCancel, saving, disabled }) {
  return (
    <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 10 }}>
      <button className="wd-btn wd-btn-ghost wd-btn-sm" onClick={onCancel} disabled={saving}>Cancel</button>
      <button className="wd-btn wd-btn-primary wd-btn-sm" onClick={onSave} disabled={saving || disabled}>
        {saving ? "Saving…" : "Save"}
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Dismantling timer — shows elapsed time and a 3-day limit bar
// ─────────────────────────────────────────────────────────────────────────────
function DismantlingTimer({ approvedDate }) {
  const LIMIT_HOURS = 72; // 3 days
 
  const elapsedMs    = Date.now() - new Date(approvedDate).getTime();
  const elapsedHours = Math.floor(elapsedMs / 3600000);
  const elapsedDays  = Math.floor(elapsedHours / 24);
  const elapsedHrsRem = elapsedHours % 24;
  const pct          = Math.min((elapsedHours / LIMIT_HOURS) * 100, 100);
  const overdue      = elapsedHours >= LIMIT_HOURS;
 
  const barColor = pct < 50
    ? "var(--wd-green)"
    : pct < 80
    ? "var(--wd-yellow)"
    : "var(--wd-red)";
 
  return (
    <div style={{
      marginTop: 10, padding: "10px 12px", borderRadius: 10,
      background: overdue ? "rgba(239,68,68,.06)" : "rgba(59,130,246,.05)",
      border: `1px solid ${overdue ? "rgba(239,68,68,.2)" : "rgba(59,130,246,.15)"}`,
    }}>
      {/* Header row */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 13 }}>{overdue ? "⚠️" : "⏱️"}</span>
          <span style={{ fontSize: 12, fontWeight: 600, color: overdue ? "var(--wd-red)" : "var(--wd-t1)" }}>
            Dismantling Time
          </span>
        </div>
        <div style={{ textAlign: "right" }}>
          <span style={{
            fontSize: 13, fontWeight: 700,
            color: overdue ? "var(--wd-red)" : "var(--wd-t1)",
            fontFamily: "var(--wd-mono)",
          }}>
            {elapsedDays}d {elapsedHrsRem}h
          </span>
          <span style={{ fontSize: 11, color: "var(--wd-t3)", marginLeft: 4 }}>/ 3 days limit</span>
        </div>
      </div>
 
      {/* Progress bar */}
      <div style={{
        height: 6, borderRadius: 3,
        background: "var(--wd-hover)",
        overflow: "hidden",
      }}>
        <div style={{
          height: "100%", borderRadius: 3,
          width: `${pct}%`,
          background: barColor,
          transition: "width .3s ease",
        }} />
      </div>
 
      {/* Labels */}
      <div style={{
        display: "flex", justifyContent: "space-between",
        marginTop: 5, fontSize: 10, color: "var(--wd-t3)",
      }}>
        <span>Approved {new Date(approvedDate).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}</span>
        <span style={{ color: overdue ? "var(--wd-red)" : "var(--wd-t3)", fontWeight: overdue ? 600 : 400 }}>
          {overdue ? `${elapsedHours - LIMIT_HOURS}h overdue` : `${Math.max(0, LIMIT_HOURS - elapsedHours)}h remaining`}
        </span>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Step 1 — Dismantling Approval
// ─────────────────────────────────────────────────────────────────────────────
export function StepDismantlingApproval({ caseInfo, onRefetch }) {
  const done = !!caseInfo.dismantlingApprovedDate;
  const [open,   setOpen]   = useState(false);
  const [date,   setDate]   = useState(caseInfo.dismantlingApprovedDate?.slice(0,10) || new Date().toISOString().slice(0,10));
  const [saving, setSaving] = useState(false);

  const updateCaseStatus = useUpdateCaseStatus();
 
  const save = useCallback(async () => {
    setSaving(true);
    try {
      // 🔌
      await updateCaseStatus.mutateAsync({ 
        caseId: caseInfo.accidentCaseNo,
        status: "DISMANTLING_APPROVED",
        description: `Dismantling approved on ${date}`,
        statusDate: date,
      });
      await new Promise(r => setTimeout(r, 700));
      onRefetch?.({ dismantlingApprovedDate: date, currentStatus: "DISMANTLING_APPROVED" });
      setOpen(false);
    } catch(e) { console.error(e); }
    setSaving(false);
  }, [date, onRefetch]);
 
  return (
    <StepCard state={done ? "done" : "active"}>
      <StepHeader
        icon={done ? "✅" : "🔨"}
        title="Dismantling Approval"
        subtitle={done ? `Approved on ${formatDate(caseInfo.dismantlingApprovedDate)}` : "Admin needs to approve dismantling to proceed"}
        badge={done ? "Approved" : "Action Required"}
        badgeColor={done ? "var(--wd-green)" : "var(--wd-blue)"}
      />
      {!done && !open && (
        <div style={{ marginTop: 10 }}>
          <button className="wd-btn wd-btn-primary wd-btn-sm" onClick={() => setOpen(true)}>
            ✓ Mark Dismantling Approved
          </button>
        </div>
      )}
      {!done && open && (
        <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 10 }}>
          <div>
            <StepLabel>Approval Date *</StepLabel>
            <input className="wd-input" type="date" value={date} max={new Date().toISOString().slice(0,10)}
              onChange={e => setDate(e.target.value)} style={{ maxWidth: 200 }} />
          </div>
          <SaveRow onSave={save} onCancel={() => setOpen(false)} saving={saving} disabled={!date} />
        </div>
      )}
    </StepCard>
  );
}
 
// ─────────────────────────────────────────────────────────────────────────────
// Step 2 — Dismantling Completed
// ─────────────────────────────────────────────────────────────────────────────
export function StepDismantlingCompleted({ caseInfo, onRefetch }) {
  const approvedDate = caseInfo.dismantlingApprovedDate;
  const done         = !!caseInfo.dismantlingCompletedDate;
  const daysPending  = !done && approvedDate ? daysSince(approvedDate) : null;
  const overdue      = daysPending !== null && daysPending >= 3;
  const updateCaseStatus = useUpdateCaseStatus();
 
  const [open,    setOpen]    = useState(false);
  const [date,    setDate]    = useState(caseInfo.dismantlingCompletedDate?.slice(0,10) || new Date().toISOString().slice(0,10));
  const [saving,  setSaving]  = useState(false);
  const [mailing, setMailing] = useState(false);
  const [mailed,  setMailed]  = useState(false);
 
  const save = useCallback(async () => {
    setSaving(true);
    try {
      await updateCaseStatus.mutateAsync({ 
        caseId: caseInfo.accidentCaseNo,
        status: "DISMANTLING_COMPLETED",
        description: `Dismantling completed on ${date}`,
        statusDate: date,
      });
      onRefetch?.({ dismantlingCompletedDate: date, currentStatus: "DISMANTLING_COMPLETED" });
      setOpen(false);
    } catch(e) { console.error(e); }
    setSaving(false);
  }, [date, onRefetch]);
 
  const sendMail = useCallback(async () => {
    setMailing(true);
    // 🔌 await sendWorkshopReminder.mutateAsync({ caseId: caseInfo.accidentCaseNo, type: "DISMANTLING_REMINDER" });
    await new Promise(r => setTimeout(r, 600));
    setMailed(true);
    setMailing(false);
  }, []);
 
  const state = done ? "done" : overdue ? "warning" : approvedDate ? "active" : "pending";
 
  return (
    <StepCard state={state}>
      <StepHeader
        icon={done ? "✅" : overdue ? "⚠️" : "🔨"}
        title="Dismantling Completed"
        subtitle={
          done      ? `Completed on ${formatDate(caseInfo.dismantlingCompletedDate)}` :
          overdue   ? `Pending for ${daysPending} days — send a reminder to the workshop` :
          approvedDate ? "Waiting for workshop to complete dismantling" :
          "Waiting for dismantling approval first"
        }
        badge={done ? "Completed" : overdue ? `${daysPending}d Overdue` : approvedDate ? "In Progress" : "Waiting"}
        badgeColor={done ? "var(--wd-green)" : overdue ? "var(--wd-yellow)" : approvedDate ? "var(--wd-blue)" : "var(--wd-t3)"}
      />
 
      {/* Dismantling countdown timer */}
      {!done && approvedDate && (
        <DismantlingTimer approvedDate={approvedDate} />
      )}
 
      {/* Overdue reminder */}
      {!done && overdue && (
        <div style={{
          marginTop: 10, padding: "8px 12px", borderRadius: 8,
          background: "rgba(234,179,8,.08)", border: "1px solid rgba(234,179,8,.2)",
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
        }}>
          <span style={{ fontSize: 12, color: "var(--wd-yellow)" }}>
            Dismantling not updated in {daysPending} days.
          </span>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {mailed && <span style={{ fontSize: 11, color: "var(--wd-green)" }}>✓ Reminder sent</span>}
            <MailButton label={mailing ? "Sending…" : "Send Reminder to Workshop"} onClick={sendMail} disabled={mailing || mailed} />
          </div>
        </div>
      )}
 
      {/* Admin can also mark done regardless */}
      {!done && approvedDate && !open && (
        <div style={{ marginTop: 10, display: "flex", gap: 8 }}>
          <button className="wd-btn wd-btn-primary wd-btn-sm" onClick={() => setOpen(true)}>
            ✓ Mark Dismantling Completed
          </button>
        </div>
      )}
      {!done && open && (
        <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 10 }}>
          <div>
            <StepLabel>Completion Date *</StepLabel>
            <input className="wd-input" type="date" value={date} max={new Date().toISOString().slice(0,10)}
              onChange={e => setDate(e.target.value)} style={{ maxWidth: 200 }} />
          </div>
          <SaveRow onSave={save} onCancel={() => setOpen(false)} saving={saving} disabled={!date} />
        </div>
      )}
    </StepCard>
  );
}
 
// ─────────────────────────────────────────────────────────────────────────────
// Step 3 — 2nd Workshop Visit (assign using workshop surveyor)
// ─────────────────────────────────────────────────────────────────────────────
export function StepSecondVisit({ caseInfo, workshopSurveyor, onRefetch,readOnly= false }) {
  const done = !!caseInfo.workshopVisit2Date;
  const [open,   setOpen]   = useState(false);
  const [saving, setSaving] = useState(false);
  const [date,   setDate]   = useState(caseInfo.workshopVisit2Date?.slice(0,10) || new Date().toISOString().slice(0,10));
  const [note,   setNote]   = useState("");

  const updateCaseStatus = useUpdateCaseStatus();
 
  const save = useCallback(async () => {
    setSaving(true);
    try {
      
      await updateCaseStatus.mutateAsync({ 
        caseId: caseInfo.accidentCaseNo,
        status: "SECOND_SURVEY_ASSIGNED",
        description: `second survey assigned on ${date}`,
        statusDate: date,
      });
      onRefetch?.({ workshopVisit2Date: date, currentStatus: "SECOND_SURVEY_ASSIGNED" });
      setOpen(false);
    } catch(e) { console.error(e); }
    setSaving(false);
  }, [date, note, onRefetch]);
 
  return (
    <StepCard state={done ? "done" : "active"}>
      <StepHeader
        icon={done ? "✅" : "👁️"}
        title="2nd Workshop Visit"
        subtitle={done ? `Scheduled on ${formatDate(caseInfo.workshopVisit2Date)}` : "Assign a 2nd workshop visit with the workshop surveyor"}
         badge={done ? "Assigned" : readOnly ? null : "Action Required"}
        badgeColor={done ? "var(--wd-green)" : "var(--wd-blue)"}
      />
 
      {/* Surveyor info auto-filled from workshop survey */}
      {workshopSurveyor && (
        <div style={{
          marginTop: 10, padding: "8px 12px", borderRadius: 8,
          background: "var(--wd-hover)", display: "flex", alignItems: "center", gap: 12,
        }}>
          <span style={{ fontSize: 13 }}>👤</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, color: "var(--wd-t3)", fontWeight: 600 }}>Surveyor (from Workshop Survey)</div>
            <div style={{ fontSize: 13, color: "var(--wd-t1)", fontWeight: 500 }}>
              {workshopSurveyor.surveyorName || workshopSurveyor.surveyorId || "—"}
            </div>
          </div>
          {workshopSurveyor.surveyorId && (
            <div style={{ fontFamily: "var(--wd-mono)", fontSize: 11, color: "var(--wd-t3)" }}>
              {workshopSurveyor.surveyorId}
            </div>
          )}
        </div>
      )}
 
       {!done && !readOnly && !open && (
        <div style={{ marginTop: 10 }}>
          <button className="wd-btn wd-btn-primary wd-btn-sm" onClick={() => setOpen(true)}>
            📅 Assign 2nd Visit
          </button>
        </div>
      )}
      {!done && open && (
        <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div>
              <StepLabel>Visit Date *</StepLabel>
              <input className="wd-input" type="date" value={date}
                onChange={e => setDate(e.target.value)} />
            </div>
          </div>
          <div>
            <StepLabel>Note</StepLabel>
            <textarea className="wd-input" rows={2} style={{ resize: "vertical", fontSize: 13 }}
              placeholder="Any instructions for the surveyor…"
              value={note} onChange={e => setNote(e.target.value)} />
          </div>
          <SaveRow onSave={save} onCancel={() => setOpen(false)} saving={saving} disabled={!date} />
        </div>
      )}
    </StepCard>
  );
}
 
// ─────────────────────────────────────────────────────────────────────────────
// Step 4 — Revised / Supplementary Estimate (optional)
// ─────────────────────────────────────────────────────────────────────────────
export function StepRevisedEstimate({ caseInfo, onRefetch }) {
  const done       = !!caseInfo.revisedEstimateAmount || !!caseInfo.revisedEstimateDoc;
  const [open,     setOpen]     = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [amount,   setAmount]   = useState(caseInfo.revisedEstimateAmount || "");
  const [docName,  setDocName]  = useState(caseInfo.revisedEstimateDoc || "");
  const [note,     setNote]     = useState(caseInfo.revisedEstimateNote || "");
 
  const save = useCallback(async () => {
    setSaving(true);
    try {
      // 🔌 await updateCaseInfo.mutateAsync({ revisedEstimateAmount: amount, revisedEstimateDoc: docName, revisedEstimateNote: note });
      await new Promise(r => setTimeout(r, 700));
      onRefetch?.({ revisedEstimateAmount: amount, revisedEstimateDoc: docName });
      setOpen(false);
    } catch(e) { console.error(e); }
    setSaving(false);
  }, [amount, docName, note, onRefetch]);
 
  return (
    <StepCard state={done ? "done" : "info"}>
      <StepHeader
        icon={done ? "✅" : "📋"}
        title="Revised / Supplementary Estimate"
        subtitle={
          done
            ? `Amount: ${caseInfo.revisedEstimateAmount ? formatINR(caseInfo.revisedEstimateAmount) : "—"}${caseInfo.revisedEstimateDoc ? ` · Doc: ${caseInfo.revisedEstimateDoc}` : ""}`
            : "Optional — surveyor may request a revised estimate after 2nd visit"
        }
        badge={done ? "Uploaded" : "Optional"}
        badgeColor={done ? "var(--wd-green)" : "var(--wd-purple, #a855f7)"}
      />
 
      {!open && (
        <div style={{ marginTop: 10 }}>
          <button className="wd-btn wd-btn-ghost wd-btn-sm" onClick={() => setOpen(true)}>
            {done ? "✏️ Edit Estimate" : "＋ Add Revised Estimate"}
          </button>
        </div>
      )}
      {open && (
        <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div>
              <StepLabel>Revised Amount (₹)</StepLabel>
              <input className="wd-input" type="number" placeholder="e.g. 95000"
                value={amount} onChange={e => setAmount(e.target.value)} />
            </div>
            <div>
              <StepLabel>Document / File Name</StepLabel>
              <input className="wd-input" type="text" placeholder="e.g. revised_estimate.pdf"
                value={docName} onChange={e => setDocName(e.target.value)} />
            </div>
          </div>
          <div>
            <StepLabel>Note</StepLabel>
            <textarea className="wd-input" rows={2} style={{ resize: "vertical", fontSize: 13 }}
              placeholder="Why is a revised estimate needed?"
              value={note} onChange={e => setNote(e.target.value)} />
          </div>
          <SaveRow onSave={save} onCancel={() => setOpen(false)} saving={saving} disabled={false} />
        </div>
      )}
    </StepCard>
  );
}
 
// ─────────────────────────────────────────────────────────────────────────────
// Step 5 — Mark 2nd Survey Done
// ─────────────────────────────────────────────────────────────────────────────
export function StepSecondSurveyDone({ caseInfo, onRefetch,readOnly= false }) {
  const done = !!caseInfo.workshopVisit2CompletedDate;
  const [open,   setOpen]   = useState(false);
  const [saving, setSaving] = useState(false);
  const [date,   setDate]   = useState(caseInfo.workshopVisit2CompletedDate?.slice(0,10) || new Date().toISOString().slice(0,10));
  const updateCaseStatus = useUpdateCaseStatus();
 
  const save = useCallback(async () => {
    setSaving(true);
    try {
       await updateCaseStatus.mutateAsync({ 
        caseId: caseInfo.accidentCaseNo,
        status: "SECOND_SURVEY_COMPLETED",
        description: `second survey completed on ${date}`,
        statusDate: date,
      });
      onRefetch?.({ workshopVisit2CompletedDate: date, currentStatus: "SECOND_SURVEY_COMPLETED" });
      setOpen(false);
    } catch(e) { console.error(e); }
    setSaving(false);
  }, [date, onRefetch]);
 
  return (
    <StepCard state={done ? "done" : "active"}>
      <StepHeader
        icon={done ? "✅" : "🔍"}
        title="2nd Workshop Survey Done"
        subtitle={done ? `Completed on ${formatDate(caseInfo.workshopVisit2CompletedDate)}` : "Mark the 2nd workshop survey as completed"}
        badge={done ? "Completed" :readOnly ? null: "Pending"}
        badgeColor={done ? "var(--wd-green)" : "var(--wd-orange)"}
      />
      {!done && !readOnly && !open && (
        <div style={{ marginTop: 10 }}>
          <button className="wd-btn wd-btn-primary wd-btn-sm" onClick={() => setOpen(true)}>
            ✓ Mark Survey Done
          </button>
        </div>
      )}
      {!done && open && (
        <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 10 }}>
          <div>
            <StepLabel>Completion Date *</StepLabel>
            <input className="wd-input" type="date" value={date} max={new Date().toISOString().slice(0,10)}
              onChange={e => setDate(e.target.value)} style={{ maxWidth: 200 }} />
          </div>
          <SaveRow onSave={save} onCancel={() => setOpen(false)} saving={saving} disabled={!date} />
        </div>
      )}
    </StepCard>
  );
}
 
// ─────────────────────────────────────────────────────────────────────────────
// Step 6 — Final Approval to Start Work
// ─────────────────────────────────────────────────────────────────────────────
export function StepFinalApproval({ caseInfo, onRefetch, readOnly= false }) {
  const done = !!caseInfo.finalApprovalDate || caseInfo.currentStatus === "REPAIR_APPROVED";
  const [open,   setOpen]   = useState(false);
  const [saving, setSaving] = useState(false);
  const [date,   setDate]   = useState(caseInfo.finalApprovalDate?.slice(0,10) || new Date().toISOString().slice(0,10));

   const updateCaseStatus = useUpdateCaseStatus();
 
  const save = useCallback(async () => {
    setSaving(true);
    try {
       await updateCaseStatus.mutateAsync({ 
        caseId: caseInfo.accidentCaseNo,
        status: "REPAIR_APPROVED",
        description: `final approval received on ${date}`,
        statusDate: date,
      });
      onRefetch?.({ finalApprovalDate: date, currentStatus: "REPAIR_APPROVED" });
      setOpen(false);
    } catch(e) { console.error(e); }
    setSaving(false);
  }, [date, onRefetch]);
 
  return (
    <StepCard state={done ? "done" : "warning"}>
      <StepHeader
        icon={done ? "✅" : "⏳"}
        title="Final Approval to Start Work"
        subtitle={done ? `Approved on ${formatDate(caseInfo.finalApprovalDate)}` : "Waiting for surveyor's final approval — update once received"}
        badge={done ? "Approved" : readOnly ? null : "Waiting for Surveyor"}
        badgeColor={done ? "var(--wd-green)" : "var(--wd-yellow)"}
      />
      {!done && !open && !readOnly && (
        <div style={{ marginTop: 10 }}>
          <button className="wd-btn wd-btn-primary wd-btn-sm" onClick={() => setOpen(true)}>
            ✓ Mark as Approved
          </button>
        </div>
      )}
      {!done && open && (
        <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 10 }}>
          <div>
            <StepLabel>Approval Date *</StepLabel>
            <input className="wd-input" type="date" value={date} max={new Date().toISOString().slice(0,10)}
              onChange={e => setDate(e.target.value)} style={{ maxWidth: 200 }} />
          </div>
          <SaveRow onSave={save} onCancel={() => setOpen(false)} saving={saving} disabled={!date} />
        </div>
      )}
    </StepCard>
  );
}
// ─────────────────────────────────────────────────────────────────────────────
// Step 7 — Work Started
// ─────────────────────────────────────────────────────────────────────────────
export function StepWorkStarted({ caseInfo, onRefetch, readOnly= false }) {
  const done = !!caseInfo.workStartedDate;
  const [open,   setOpen]   = useState(false);
  const [saving, setSaving] = useState(false);
  const [date,   setDate]   = useState(caseInfo.workStartedDate?.slice(0,10) || new Date().toISOString().slice(0,10));
 
  const updateCaseStatus = useUpdateCaseStatus();

  const save = useCallback(async () => {
    setSaving(true);
    try {
       await updateCaseStatus.mutateAsync({ 
        caseId: caseInfo.accidentCaseNo,
        status: "REPAIR_STARTED",
        description: `work started on ${date}`,
        statusDate: date,
      });
      onRefetch?.({ workStartedDate: date, currentStatus: "REPAIR_STARTED" });
      setOpen(false);
    } catch(e) { console.error(e); }
    setSaving(false);
  }, [date, onRefetch]);
 
  return (
    <StepCard state={done ? "done" : "active"}>
      <StepHeader
        icon={done ? "✅" : "🔧"}
        title="Workshop Started Work"
        subtitle={done ? `Started on ${formatDate(caseInfo.workStartedDate)}` : "Workshop to update when repair work begins"}
        badge={done ? "In Progress" : readOnly ? null : "Pending"}
        badgeColor={done ? "var(--wd-green)" : "var(--wd-blue)"}
      />
      {!done && !open && !readOnly && (
        <div style={{ marginTop: 10 }}>
          <button className="wd-btn wd-btn-ghost wd-btn-sm" onClick={() => setOpen(true)}>
            ✓ Mark Work Started (Admin Override)
          </button>
        </div>
      )}
      {!done && open && (
        <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 10 }}>
          <div>
            <StepLabel>Start Date *</StepLabel>
            <input className="wd-input" type="date" value={date} max={new Date().toISOString().slice(0,10)}
              onChange={e => setDate(e.target.value)} style={{ maxWidth: 200 }} />
          </div>
          <SaveRow onSave={save} onCancel={() => setOpen(false)} saving={saving} disabled={!date} />
        </div>
      )}
    </StepCard>
  );
}
 
// ─────────────────────────────────────────────────────────────────────────────
// Step 8 — Truck Repaired + Ready for Reinspection
// ─────────────────────────────────────────────────────────────────────────────
export function StepTruckRepaired({ caseInfo, workshopSurveyor, onRefetch, readOnly= false }) {
  const repaired     = !!caseInfo.truckFinalizedDate;
  const [open,       setOpen]    = useState(false);
  const [saving,     setSaving]  = useState(false);
  const [mailing,    setMailing] = useState(false);
  const [mailed,     setMailed]  = useState(false);
  const [date,       setDate]    = useState(caseInfo.truckFinalizedDate?.slice(0,10) || new Date().toISOString().slice(0,10));
 
  const updateCaseStatus = useUpdateCaseStatus();
  
  const save = useCallback(async () => {
    setSaving(true);
    try {
      await updateCaseStatus.mutateAsync({ 
        caseId: caseInfo.accidentCaseNo,
        status: "REPAIR_COMPLETED",
        description: `truck repaired on ${date}`,
        statusDate: date,
      });
      onRefetch?.({ truckFinalizedDate: date, currentStatus: "REPAIR_COMPLETED" });
      setOpen(false);
    } catch(e) { console.error(e); }
    setSaving(false);
  }, [date, onRefetch]);
 
  const sendMail = useCallback(async () => {
    setMailing(true);
    // 🔌 await sendSurveyorReinspectionMail.mutateAsync({ caseId: caseInfo.accidentCaseNo });
    await new Promise(r => setTimeout(r, 700));
    setMailed(true);
    setMailing(false);
  }, []);
 
  return (
    <StepCard state={repaired ? "done" : "active"}>
      <StepHeader
        icon={repaired ? "✅" : "🚗"}
        title="Truck Repaired"
        subtitle={repaired ? `Repaired on ${formatDate(caseInfo.truckFinalizedDate)} · Ready for reinspection` : "Workshop to update when repair is complete"}
        badge={repaired ? "Repaired" : readOnly ? null : "In Repair"}
        badgeColor={repaired ? "var(--wd-green)" : "var(--wd-blue)"}
      />
 
      {/* Send mail to surveyor for reinspection */}
      {repaired && (
        <div style={{
          marginTop: 10, padding: "8px 12px", borderRadius: 8,
          background: "rgba(59,130,246,.07)", border: "1px solid rgba(59,130,246,.15)",
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
        }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: "var(--wd-blue)" }}>Ready for Reinspection</div>
            {workshopSurveyor && (
              <div style={{ fontSize: 11, color: "var(--wd-t3)", marginTop: 1 }}>
                Surveyor: {workshopSurveyor.surveyorName || workshopSurveyor.surveyorId || "—"}
              </div>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {mailed && <span style={{ fontSize: 11, color: "var(--wd-green)" }}>✓ Mail sent</span>}
            <MailButton
              label={mailing ? "Sending…" : "Send Mail to Surveyor"}
              onClick={sendMail}
              disabled={mailing || mailed}
            />
          </div>
        </div>
      )}
 
      {!repaired && !open && !readOnly && (
        <div style={{ marginTop: 10 }}>
          <button className="wd-btn wd-btn-ghost wd-btn-sm" onClick={() => setOpen(true)}>
            ✓ Mark as Repaired (Admin Override)
          </button>
        </div>
      )}
      {!repaired && open && (
        <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 10 }}>
          <div>
            <StepLabel>Repair Completion Date *</StepLabel>
            <input className="wd-input" type="date" value={date} max={new Date().toISOString().slice(0,10)}
              onChange={e => setDate(e.target.value)} style={{ maxWidth: 200 }} />
          </div>
          <SaveRow onSave={save} onCancel={() => setOpen(false)} saving={saving} disabled={!date} />
        </div>
      )}
    </StepCard>
  );
}
 
// ─────────────────────────────────────────────────────────────────────────────
// Step 9 — Reinspection Done
// ─────────────────────────────────────────────────────────────────────────────
export function StepReinspection({ caseInfo, onRefetch }) {
  const done = !!caseInfo.readyForInspectionDate || caseInfo.currentStatus === "REINSPECTION_COMPLETED";
  const [open,   setOpen]   = useState(false);
  const [saving, setSaving] = useState(false);
  const [date,   setDate]   = useState(caseInfo.readyForInspectionDate?.slice(0,10) || new Date().toISOString().slice(0,10));
 
  const updateCaseStatus = useUpdateCaseStatus();

  const save = useCallback(async () => {
    setSaving(true);
    try {
      await updateCaseStatus.mutateAsync({ 
        caseId: caseInfo.accidentCaseNo,
        status: "REINSPECTION_COMPLETED",
        description: `reinspection completed on ${date}`,
        statusDate: date,
      });
      onRefetch?.({ readyForInspectionDate: date, currentStatus: "REINSPECTION_COMPLETED" });
      setOpen(false);
    } catch(e) { console.error(e); }
    setSaving(false);
  }, [date, onRefetch]);
 
  return (
    <StepCard state={done ? "done" : "active"}>
      <StepHeader
        icon={done ? "✅" : "🔍"}
        title="Reinspection Done"
        subtitle={done ? `Completed on ${formatDate(caseInfo.readyForInspectionDate)}` : "Update once surveyor has completed reinspection"}
        badge={done ? "Done" : "Pending"}
        badgeColor={done ? "var(--wd-green)" : "var(--wd-orange)"}
      />
      {!done && !open && (
        <div style={{ marginTop: 10 }}>
          <button className="wd-btn wd-btn-primary wd-btn-sm" onClick={() => setOpen(true)}>
            ✓ Mark Reinspection Done
          </button>
        </div>
      )}
      {!done && open && (
        <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 10 }}>
          <div>
            <StepLabel>Reinspection Date *</StepLabel>
            <input className="wd-input" type="date" value={date} max={new Date().toISOString().slice(0,10)}
              onChange={e => setDate(e.target.value)} style={{ maxWidth: 200 }} />
          </div>
          <SaveRow onSave={save} onCancel={() => setOpen(false)} saving={saving} disabled={!date} />
        </div>
      )}
    </StepCard>
  );
}
 
// ─────────────────────────────────────────────────────────────────────────────
// Step 10 — Waiting for Proforma Invoice
// ─────────────────────────────────────────────────────────────────────────────
export function StepProforma({ caseInfo, onRefetch, readOnly = false }) {

  const handleProforma = useHandleProforma();
  
  // Detect if proforma was already skipped (no docs but status is past proforma)
  const wasSkippedViaUnauthorized =
    (caseInfo.currentStatus === "PROFORMA_UPLOADED" ||
     ["PROFORMA_RECEIVED","PAYMENT_REQUEST_SENT","PAYMENT_COMPLETED",
      "CLAIM_SETTLED","CASE_CLOSED"].includes(caseInfo.currentStatus)) &&
    !caseInfo.proformaDoc;

  const [skipProforma, setSkipProforma] = useState(() => wasSkippedViaUnauthorized);

  // Three sub-states
  const uploaded = !!caseInfo.proformaDoc;
  const approved = !!caseInfo.proformaApprovedDate || 
    ["PROFORMA_RECEIVED","PAYMENT_REQUEST_SENT","PAYMENT_COMPLETED",
     "CLAIM_SETTLED","CASE_CLOSED"].includes(caseInfo.currentStatus);

  const [open,     setOpen]     = useState(false);
  const [file,     setFile]     = useState(null);
  const [amount,   setAmount]   = useState(caseInfo.proformaAmount || "");
  const [dragOver, setDragOver] = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [approving,setApproving]= useState(false);
  const [mailSent, setMailSent] = useState(false);

  const handleUpload = useCallback(async () => {
    if (!file) return;
    setSaving(true);
    try {
      const result = await handleProforma.mutateAsync({
        caseId: caseInfo.accidentCaseNo,
        type: "UPLOAD",
        file,
        amount,
      });
      onRefetch?.({ 
        proformaDoc: result?.fileUrl || file.name,
        proformaAmount: amount,
        currentStatus: "PROFORMA_UPLOADED",
      });
      setOpen(false);
      setFile(null);
    } catch(e) { console.error(e); }
    setSaving(false);
  }, [file, amount, caseInfo, handleProforma, onRefetch]);

  const handleApprove = useCallback(async () => {
    setApproving(true);
    try {
      const result = await handleProforma.mutateAsync({
        caseId: caseInfo.accidentCaseNo,
        type: "APPROVE",
      });
      setMailSent(!!result?.email?.sent);
      onRefetch?.({ 
        proformaApprovedDate: new Date().toISOString(),
        currentStatus: "PROFORMA_RECEIVED",
      });
    } catch(e) { console.error(e); }
    setApproving(false);
  }, [caseInfo, handleProforma, onRefetch]);

  const handleEnableSkip = useCallback(() => {
    setSkipProforma(true);
    onRefetch?.({
      proformaDoc: null,
      proformaAmount: null,
      proformaApprovedDate: new Date().toISOString(),
      currentStatus: "PROFORMA_RECEIVED",
    });
  }, [onRefetch]);

  const handleDisableSkip = useCallback(() => {
    setSkipProforma(false);
    onRefetch?.({
      proformaDoc: null,
      proformaAmount: null,
      proformaApprovedDate: null,
      currentStatus: "REINSPECTION_COMPLETED",
    });
  }, [onRefetch]);

  // ── State: nothing uploaded yet
  if (!uploaded && !skipProforma) {
    return (
      <StepCard state="warning">
        <StepHeader
          icon="📄"
          title="Proforma Invoice"
          subtitle="Waiting for workshop to upload the proforma invoice"
          badge="Waiting for Workshop"
          badgeColor="var(--wd-yellow)"
        />

        {/* Workshop authorization toggle */}
        {!readOnly && !uploaded && (
          <div style={{
            marginTop: 12,
            padding: "10px 12px",
            borderRadius: 10,
            background: skipProforma ? "rgba(168,85,247,.06)" : "var(--wd-hover)",
            border: skipProforma
              ? "1px solid rgba(168,85,247,.22)"
              : "1px solid var(--wd-border)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}>
            <div>
              <div style={{
                fontSize: 11, fontWeight: 700,
                color: skipProforma ? "var(--wd-purple, #a855f7)" : "var(--wd-t2)",
              }}>
                Does workshop provide proforma?
              </div>
              <div style={{ fontSize: 10, color: "var(--wd-t3)", marginTop: 1 }}>
                {skipProforma
                  ? "Proforma step skipped"
                  : "Can the workshop provide a proforma invoice?"}
              </div>
            </div>

            {/* Segmented control */}
            <div style={{
              display: "inline-flex",
              borderRadius: 8,
              border: "1px solid var(--wd-border-md)",
              overflow: "hidden",
              flexShrink: 0,
            }}>
              <button
                onClick={handleDisableSkip}
                style={{
                  padding: "5px 13px",
                  fontSize: 11, fontWeight: 600,
                  border: "none",
                  borderRight: "1px solid var(--wd-border-md)",
                  cursor: "pointer",
                  transition: "all .15s",
                  background: !skipProforma ? "var(--wd-blue)" : "var(--wd-hover)",
                  color:      !skipProforma ? "#fff"           : "var(--wd-t2)",
                }}
              >
                📋 Yes
              </button>
              <button
                onClick={handleEnableSkip}
                style={{
                  padding: "5px 13px",
                  fontSize: 11, fontWeight: 600,
                  border: "none",
                  cursor: "pointer",
                  transition: "all .15s",
                  background: skipProforma ? "var(--wd-purple, #a855f7)" : "var(--wd-hover)",
                  color:      skipProforma ? "#fff"                       : "var(--wd-t2)",
                }}
              >
                ✗ No
              </button>
            </div>
          </div>
        )}

        {!readOnly && !open && !skipProforma && (
          <div style={{ marginTop: 10 }}>
            <button className="wd-btn wd-btn-ghost wd-btn-sm" onClick={() => setOpen(true)}>
              📤 Upload Proforma PDF (Admin Override)
            </button>
          </div>
        )}
        {open && <UploadForm 
          file={file} setFile={setFile} amount={amount} setAmount={setAmount}
          dragOver={dragOver} setDragOver={setDragOver}
          saving={saving} onSave={handleUpload} onCancel={() => setOpen(false)}
        />}
      </StepCard>
    );
  }

  // ── State: skipped for unauthorized workshop
  if (skipProforma) {
    return (
      <StepCard state="skipped">
        <StepHeader
          icon="✗"
          title="Proforma Invoice"
          subtitle="Unauthorized workshop — proforma step skipped"
          badge="Skipped"
          badgeColor="var(--wd-purple, #a855f7)"
        />
        <div style={{
          marginTop: 12, padding: "14px 16px", borderRadius: 10,
          background: "rgba(168,85,247,.06)", border: "1px solid rgba(168,85,247,.18)",
          display: "flex", alignItems: "flex-start", gap: 12,
        }}>
          <span style={{ fontSize: 20, flexShrink: 0 }}>✗</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "var(--wd-purple, #a855f7)", marginBottom: 6 }}>
              Proforma step skipped
            </div>
            <div style={{ fontSize: 11, color: "var(--wd-t3)", marginTop: 1, lineHeight: 1.5 }}>
              This workshop does not provide a proforma invoice. 
              The step has been skipped and assessment will proceed directly.
            </div>
            <button
              className="wd-btn wd-btn-ghost wd-btn-sm"
              style={{ marginTop: 10, fontSize: 10 }}
              onClick={handleDisableSkip}
            >
              ↩ Revert to request proforma
            </button>
          </div>
        </div>
      </StepCard>
    );
  }

  // ── State: uploaded, pending admin approval
  // ── State: uploaded, pending admin approval
  if (uploaded && !approved) {
    return (
      <StepCard state="active">
        <StepHeader
          icon="📋"
          title="Proforma Invoice"
          subtitle="Workshop has uploaded the proforma — review and approve to notify surveyor"
          badge="Pending Approval"
          badgeColor="var(--wd-orange)"
        />

        {/* File + amount details */}
        <div style={{
          marginTop: 12, padding: "10px 14px", borderRadius: 10,
          background: "var(--wd-hover)", border: "1px solid var(--wd-border)",
          display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10,
        }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase",
              letterSpacing: ".05em", color: "var(--wd-t3)", marginBottom: 4 }}>
              Proforma PDF
            </div>
            {caseInfo.proformaDoc ? (
              <a href={caseInfo.proformaDoc} target="_blank" rel="noopener noreferrer"
                style={{ fontSize: 12, color: "var(--wd-blue)", fontWeight: 600 }}>
                View PDF ↗
              </a>
            ) : (
              <span style={{ fontSize: 12, color: "var(--wd-t3)", fontStyle: "italic" }}>
                {caseInfo.proformaDoc}
              </span>
            )}
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase",
              letterSpacing: ".05em", color: "var(--wd-t3)", marginBottom: 4 }}>
              Amount
            </div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "var(--wd-blue)" }}>
              {caseInfo.proformaAmount 
                ? formatINR(caseInfo.proformaAmount)
                : <span style={{ fontStyle: "italic", color: "var(--wd-t3)", fontSize: 12 }}>Not specified</span>
              }
            </div>
          </div>
        </div>

        {/* Approve action */}
        <div style={{
          marginTop: 12, padding: "10px 14px", borderRadius: 10,
          background: "rgba(59,130,246,.06)", border: "1px solid rgba(59,130,246,.2)",
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
        }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: "var(--wd-blue)" }}>
              Approve &amp; notify workshop surveyor
            </div>
            <div style={{ fontSize: 11, color: "var(--wd-t3)", marginTop: 2 }}>
              An email with proforma details will be sent to the surveyor. 
              7-day SLA clock starts on approval.
            </div>
          </div>
          <button
            className="wd-btn wd-btn-primary wd-btn-sm"
            style={{ whiteSpace: "nowrap" }}
            onClick={handleApprove}
            disabled={approving}
          >
            {approving ? "Approving…" : "✓ Approve & Notify"}
          </button>
        </div>
      </StepCard>
    );
  }

  // ── State: approved, surveyor notified, waiting for assessment
  return (
    <StepCard state="done">
      <StepHeader
        icon="✅"
        title="Proforma Invoice"
        subtitle={`Approved${caseInfo.proformaAmount 
          ? ` · ${formatINR(caseInfo.proformaAmount)}` : ""} · Surveyor notified`}
        badge="Awaiting Assessment"
        badgeColor="var(--wd-green)"
      />

      <div style={{
        marginTop: 12, padding: "10px 14px", borderRadius: 10,
        background: "rgba(59,130,246,.06)", border: "1px solid rgba(59,130,246,.15)",
        display: "flex", alignItems: "center", gap: 10,
      }}>
        <span style={{ fontSize: 14 }}>📧</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: "var(--wd-blue)" }}>
            Surveyor has been notified
          </div>
          <div style={{ fontSize: 11, color: "var(--wd-t3)", marginTop: 1 }}>
            7-day window for assessment. Auto-reminder will be sent to admin if no response.
          </div>
        </div>
        {mailSent && (
          <span style={{ fontSize: 11, color: "var(--wd-green)", fontWeight: 600 }}>✓ Mail sent</span>
        )}
      </div>

      {/* SLA status hint — the actual SLA panel in Timeline tab handles the auto-email */}
      <div style={{
        marginTop: 8, padding: "8px 12px", borderRadius: 8,
        background: "var(--wd-hover)", border: "1px solid var(--wd-border)",
        fontSize: 11, color: "var(--wd-t3)", lineHeight: 1.6,
      }}>
        ℹ️ If the surveyor does not respond within 7 days, an automatic notification 
        will be sent to admin. You can also manually trigger a reminder from the{" "}
        <strong style={{ color: "var(--wd-t2)" }}>Timeline → SLA Monitor</strong> section.
      </div>
    </StepCard>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// StepAssessment — Receive surveyor assessment (7-day window)
// ─────────────────────────────────────────────────────────────────────────────
export function StepAssessment({ caseInfo, onRefetch, readOnly = false }) {
  const handleAssessment = useHandleAssessment();

  const done = !!caseInfo.assessmentReceivedDate || ["ASSESSMENT_RECEIVED","ASSESSMENT_CROSSCHECKED","TAX_INVOICE_RECEIVED","PAYMENT_REQUEST_SENT","PAYMENT_COMPLETED","CLAIM_SETTLED","CASE_CLOSED"].includes(caseInfo.currentStatus);

  const [open,     setOpen]     = useState(false);
  const [file,     setFile]     = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [saving,   setSaving]   = useState(false);

  const handleReceive = useCallback(async () => {
    setSaving(true);
    try {
      const result = await handleAssessment.mutateAsync({
        caseId: caseInfo.accidentCaseNo,
        type: "RECEIVE",
        file,
      });
      onRefetch?.({
        assessmentReceivedDate: new Date().toISOString(),
        assessmentDoc: result?.fileUrl || file?.name || "uploaded",
        currentStatus: "ASSESSMENT_RECEIVED",
      });
      setOpen(false);
      setFile(null);
    } catch(e) { console.error(e); }
    setSaving(false);
  }, [file, caseInfo, handleAssessment, onRefetch]);

  // Done state — show summary
  if (done) {
    return (
      <StepCard state="done">
        <StepHeader
          icon="✅"
          title="Surveyor Assessment Received"
          subtitle={`Received on ${formatDate(caseInfo.assessmentReceivedDate)}`}
          badge="Received"
          badgeColor="var(--wd-green)"
        />
        {caseInfo.assessmentDoc && (
          <div style={{ marginTop: 10 }}>
            <a href={caseInfo.assessmentDoc} target="_blank" rel="noopener noreferrer"
              style={{ 
                fontSize: 12, color: "var(--wd-blue)", fontWeight: 600,
                display: "inline-flex", alignItems: "center", gap: 5,
              }}>
              📄 View Assessment PDF ↗
            </a>
          </div>
        )}
      </StepCard>
    );
  }

  // Waiting state
  return (
    <StepCard state="warning">
      <StepHeader
        icon="⏳"
        title="Awaiting Surveyor Assessment"
        subtitle="Surveyor has been notified. Upload the assessment once received via email."
        badge="7-Day Window"
        badgeColor="var(--wd-yellow)"
      />

      {/* 7-day countdown from proforma approval */}
      {caseInfo.proformaApprovedDate && (
        <CountdownTimer
          startDate={caseInfo.proformaApprovedDate}
          limitHours={168}
          label="Surveyor Assessment Window"
        />
      )}

      {!readOnly && !open && (
        <div style={{ marginTop: 12 }}>
          <button 
            className="wd-btn wd-btn-primary wd-btn-sm" 
            onClick={() => setOpen(true)}
          >
            📥 Mark Assessment Received
          </button>
        </div>
      )}

      {open && (
        <div style={{ 
          marginTop: 14, paddingTop: 14,
          borderTop: "1px dashed var(--wd-border)",
          display: "flex", flexDirection: "column", gap: 12 
        }}>
          <div style={{ fontSize: 12, color: "var(--wd-t2)", lineHeight: 1.6 }}>
            Upload the assessment PDF received from the surveyor via email.
          </div>

          {/* Drop zone */}
          <div
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={e => {
              e.preventDefault(); setDragOver(false);
              const f = e.dataTransfer.files[0];
              if (f?.type === "application/pdf") setFile(f);
            }}
            style={{
              border: `2px dashed ${dragOver ? "var(--wd-blue)" 
                : file ? "var(--wd-green)" : "var(--wd-border-md)"}`,
              borderRadius: 10, padding: "18px 16px",
              background: dragOver ? "rgba(59,130,246,.05)" 
                : file ? "rgba(34,197,94,.05)" : "var(--wd-hover)",
              textAlign: "center", cursor: "pointer", transition: "all .15s",
            }}
            onClick={() => document.getElementById("assessment-pdf-input").click()}
          >
            <input 
              id="assessment-pdf-input" type="file" accept="application/pdf"
              style={{ display: "none" }}
              onChange={e => {
                const f = e.target.files[0];
                if (f?.type === "application/pdf") setFile(f);
              }} 
            />
            {file ? (
              <div style={{ 
                display: "flex", alignItems: "center", 
                justifyContent: "center", gap: 8 
              }}>
                <span style={{ fontSize: 20 }}>📄</span>
                <div style={{ textAlign: "left" }}>
                  <div style={{ fontSize: 13, fontWeight: 600, 
                    color: "var(--wd-green)" }}>{file.name}</div>
                  <div style={{ fontSize: 11, color: "var(--wd-t3)" }}>
                    {(file.size / 1024).toFixed(0)} KB · PDF
                  </div>
                </div>
                <button className="wd-btn wd-btn-ghost wd-btn-sm" 
                  style={{ fontSize: 11, marginLeft: 8 }}
                  onClick={e => { e.stopPropagation(); setFile(null); }}>
                  ✕
                </button>
              </div>
            ) : (
              <>
                <div style={{ fontSize: 20, marginBottom: 6 }}>📤</div>
                <div style={{ fontSize: 13, fontWeight: 600, 
                  color: "var(--wd-t2)" }}>
                  Drop assessment PDF or click to browse
                </div>
                <div style={{ fontSize: 11, color: "var(--wd-t3)", marginTop: 2 }}>
                  PDF only — optional but recommended
                </div>
              </>
            )}
          </div>

          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button className="wd-btn wd-btn-ghost wd-btn-sm" 
              onClick={() => { setOpen(false); setFile(null); }} 
              disabled={saving}>
              Cancel
            </button>
            <button className="wd-btn wd-btn-primary wd-btn-sm" 
              onClick={handleReceive} disabled={saving}>
              {saving ? "Saving…" : "✓ Confirm Assessment Received"}
            </button>
          </div>
        </div>
      )}
    </StepCard>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// StepCrossCheck — Admin cross-checks assessment (2-day window)
// Runs in parallel with StepTaxInvoice visually once assessment is received
// ─────────────────────────────────────────────────────────────────────────────
export function StepCrossCheck({ caseInfo, onRefetch, readOnly = false }) {
  const handleAssessment = useHandleAssessment();

  const done = !!caseInfo.assessmentCrossCheckedDate || ["ASSESSMENT_CROSSCHECKED","TAX_INVOICE_RECEIVED","PAYMENT_REQUEST_SENT","PAYMENT_COMPLETED","CLAIM_SETTLED","CASE_CLOSED"].includes(caseInfo.currentStatus);

  const [open,    setOpen]    = useState(false);
  const [notes,   setNotes]   = useState("");
  const [saving,  setSaving]  = useState(false);
  const [mailing, setMailing] = useState(false);
  const [mailed,  setMailed]  = useState(false);

  const handleCrossCheck = useCallback(async () => {
    setSaving(true);
    try {
      await handleAssessment.mutateAsync({
        caseId: caseInfo.accidentCaseNo,
        type: "CROSSCHECK",
      });
      onRefetch?.({
        assessmentCrossCheckedDate: new Date().toISOString(),
        currentStatus: "ASSESSMENT_CROSSCHECKED",
      });
      setOpen(false);
    } catch(e) { console.error(e); }
    setSaving(false);
  }, [caseInfo, handleAssessment, onRefetch]);

  const sendReminder = useCallback(async () => {
    setMailing(true);
    await new Promise(r => setTimeout(r, 600));
    setMailed(true);
    setMailing(false);
  }, []);

  if (done) {
    return (
      <StepCard state="done">
        <StepHeader
          icon="✅"
          title="Assessment Cross-Checked"
          subtitle={`Verified on ${formatDate(caseInfo.assessmentCrossCheckedDate)}`}
          badge="Done"
          badgeColor="var(--wd-green)"
        />
      </StepCard>
    );
  }

  const isOverdue = caseInfo.assessmentReceivedAt && 
    (Date.now() - new Date(caseInfo.assessmentReceivedAt).getTime()) 
    > 48 * 3600000;

  return (
    <StepCard state={isOverdue ? "warning" : "active"}>
      <StepHeader
        icon={isOverdue ? "⚠️" : "🔍"}
        title="Cross-Check Assessment"
        subtitle="Review the surveyor assessment against workshop proforma — 2-day window"
        badge={isOverdue ? "Overdue" : "2-Day Window"}
        badgeColor={isOverdue ? "var(--wd-red)" : "var(--wd-blue)"}
      />

      {/* 2-day countdown */}
      {caseInfo.assessmentReceivedDate && (
        <CountdownTimer
          startDate={caseInfo.assessmentReceivedDate}
          limitHours={48}
          label="Cross-Check Window"
        />
      )}

      {/* Overdue reminder */}
      {isOverdue && (
        <div style={{
          marginTop: 10, padding: "8px 12px", borderRadius: 8,
          background: "rgba(239,68,68,.06)", border: "1px solid rgba(239,68,68,.2)",
          display: "flex", alignItems: "center", 
          justifyContent: "space-between", gap: 10,
        }}>
          <span style={{ fontSize: 12, color: "var(--wd-red)" }}>
            Cross-check overdue — auto-reminder sent to admin.
          </span>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {mailed && (
              <span style={{ fontSize: 11, color: "var(--wd-green)" }}>
                ✓ Reminder sent
              </span>
            )}
            <button 
              className="wd-btn wd-btn-ghost wd-btn-sm"
              style={{ fontSize: 11 }}
              onClick={sendReminder} 
              disabled={mailing || mailed}
            >
              📧 {mailing ? "Sending…" : "Send Manual Reminder"}
            </button>
          </div>
        </div>
      )}

      {/* What to check */}
      <div style={{
        marginTop: 10, padding: "10px 14px", borderRadius: 10,
        background: "var(--wd-hover)", border: "1px solid var(--wd-border)",
      }}>
        <div style={{ 
          fontSize: 10, fontWeight: 700, textTransform: "uppercase", 
          letterSpacing: ".05em", color: "var(--wd-t3)", marginBottom: 8 
        }}>
          Cross-check these against each other
        </div>
        <div style={{ 
          display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 
        }}>
          <div>
            <div style={{ fontSize: 10, color: "var(--wd-t3)", 
              fontWeight: 600, marginBottom: 3 }}>
              Proforma Amount
            </div>
            <div style={{ fontSize: 13, fontWeight: 700, 
              color: "var(--wd-blue)" }}>
              {caseInfo.proformaAmount 
                ? formatINR(caseInfo.proformaAmount) : "—"}
            </div>
            {caseInfo.proformaDoc && (
              <a href={caseInfo.proformaDoc} target="_blank" 
                rel="noopener noreferrer"
                style={{ fontSize: 11, color: "var(--wd-blue)" }}>
                View Proforma ↗
              </a>
            )}
          </div>
          <div>
            <div style={{ fontSize: 10, color: "var(--wd-t3)", 
              fontWeight: 600, marginBottom: 3 }}>
              Assessment PDF
            </div>
            {caseInfo.assessmentDoc ? (
              <a href={caseInfo.assessmentDoc} target="_blank" 
                rel="noopener noreferrer"
                style={{ fontSize: 12, color: "var(--wd-blue)", 
                  fontWeight: 600 }}>
                View Assessment ↗
              </a>
            ) : (
              <span style={{ fontSize: 12, color: "var(--wd-t3)", 
                fontStyle: "italic" }}>
                No PDF uploaded
              </span>
            )}
          </div>
        </div>
      </div>

      {!readOnly && !open && (
        <div style={{ marginTop: 12 }}>
          <button 
            className="wd-btn wd-btn-primary wd-btn-sm" 
            onClick={() => setOpen(true)}
          >
            ✓ Mark Cross-Check Done
          </button>
        </div>
      )}

      {open && (
        <div style={{ 
          marginTop: 12, paddingTop: 12,
          borderTop: "1px dashed var(--wd-border)",
          display: "flex", flexDirection: "column", gap: 10 
        }}>
          <div>
            <StepLabel>Notes (optional)</StepLabel>
            <textarea 
              className="wd-input" rows={2} 
              style={{ resize: "vertical", fontSize: 13 }}
              placeholder="Any discrepancies or remarks…"
              value={notes} 
              onChange={e => setNotes(e.target.value)} 
            />
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button className="wd-btn wd-btn-ghost wd-btn-sm" 
              onClick={() => setOpen(false)} disabled={saving}>
              Cancel
            </button>
            <button className="wd-btn wd-btn-primary wd-btn-sm" 
              onClick={handleCrossCheck} disabled={saving}>
              {saving ? "Saving…" : "✓ Confirm Cross-Check"}
            </button>
          </div>
        </div>
      )}
    </StepCard>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// StepTaxInvoice — Workshop uploads tax invoice (admin override available)
// Runs in parallel with StepCrossCheck — both must be done for delivery complete
// ─────────────────────────────────────────────────────────────────────────────
export function StepTaxInvoice({ caseInfo, onRefetch, readOnly = false }) {
  const handleTaxInvoice = useHandleTaxInvoice();

  const done = !!caseInfo.taxInvoiceDoc;

  const [open,     setOpen]     = useState(false);
  const [file,     setFile]     = useState(null);
  const [amount,   setAmount]   = useState(caseInfo.taxInvoiceAmount || "");
  const [dragOver, setDragOver] = useState(false);
  const [saving,   setSaving]   = useState(false);

  const handleUpload = useCallback(async () => {
    setSaving(true);
    try {
      const result = await handleTaxInvoice.mutateAsync({
        caseId: caseInfo.accidentCaseNo,
        file,
        amount,
      });
      onRefetch?.({
        taxInvoiceDoc: result?.fileUrl || file?.name || "uploaded",
        taxInvoiceAmount: amount,
        currentStatus: "TAX_INVOICE_RECEIVED",
      });
      setOpen(false);
      setFile(null);
    } catch(e) { console.error(e); }
    setSaving(false);
  }, [file, amount, caseInfo, handleTaxInvoice, onRefetch]);

  if (done) {
    return (
      <StepCard state="done">
        <StepHeader
          icon="✅"
          title="Tax Invoice Received"
          subtitle={`${caseInfo.taxInvoiceAmount 
            ? formatINR(caseInfo.taxInvoiceAmount) : ""}${
            caseInfo.taxInvoiceDoc ? " · PDF uploaded" : ""}`}
          badge="Received"
          badgeColor="var(--wd-green)"
        />
        {caseInfo.taxInvoiceDoc && (
          <div style={{ marginTop: 10 }}>
            <a href={caseInfo.taxInvoiceDoc} target="_blank" 
              rel="noopener noreferrer"
              style={{ fontSize: 12, color: "var(--wd-blue)", fontWeight: 600,
                display: "inline-flex", alignItems: "center", gap: 5 }}>
              📄 View Tax Invoice ↗
            </a>
          </div>
        )}
      </StepCard>
    );
  }

  return (
    <StepCard state="warning">
      <StepHeader
        icon="🧾"
        title="Tax Invoice"
        subtitle="Waiting for workshop to upload the GST tax invoice"
        badge="Waiting for Workshop"
        badgeColor="var(--wd-yellow)"
      />

      <div style={{
        marginTop: 10, padding: "8px 12px", borderRadius: 8,
        background: "rgba(59,130,246,.05)", border: "1px dashed rgba(59,130,246,.2)",
        fontSize: 11, color: "var(--wd-t2)", lineHeight: 1.6,
      }}>
        Workshop needs to upload the final GST tax invoice from their portal.
        Admin can override and upload manually if needed.
      </div>

      {!readOnly && !open && (
        <div style={{ marginTop: 10 }}>
          <button 
            className="wd-btn wd-btn-ghost wd-btn-sm" 
            onClick={() => setOpen(true)}
          >
            📤 Upload Tax Invoice (Admin Override)
          </button>
        </div>
      )}

      {open && (
        <div style={{ 
          marginTop: 12, paddingTop: 12,
          borderTop: "1px dashed var(--wd-border)",
          display: "flex", flexDirection: "column", gap: 12 
        }}>
          <div
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={e => {
              e.preventDefault(); setDragOver(false);
              const f = e.dataTransfer.files[0];
              if (f?.type === "application/pdf") setFile(f);
            }}
            style={{
              border: `2px dashed ${dragOver ? "var(--wd-blue)" 
                : file ? "var(--wd-green)" : "var(--wd-border-md)"}`,
              borderRadius: 10, padding: "18px 16px",
              background: dragOver ? "rgba(59,130,246,.05)" 
                : file ? "rgba(34,197,94,.05)" : "var(--wd-hover)",
              textAlign: "center", cursor: "pointer", transition: "all .15s",
            }}
            onClick={() => document.getElementById("tax-invoice-pdf-input").click()}
          >
            <input 
              id="tax-invoice-pdf-input" type="file" accept="application/pdf"
              style={{ display: "none" }}
              onChange={e => {
                const f = e.target.files[0];
                if (f?.type === "application/pdf") setFile(f);
              }} 
            />
            {file ? (
              <div style={{ 
                display: "flex", alignItems: "center", 
                justifyContent: "center", gap: 8 
              }}>
                <span style={{ fontSize: 20 }}>📄</span>
                <div style={{ textAlign: "left" }}>
                  <div style={{ fontSize: 13, fontWeight: 600, 
                    color: "var(--wd-green)" }}>{file.name}</div>
                  <div style={{ fontSize: 11, color: "var(--wd-t3)" }}>
                    {(file.size / 1024).toFixed(0)} KB
                  </div>
                </div>
                <button className="wd-btn wd-btn-ghost wd-btn-sm" 
                  style={{ fontSize: 11 }}
                  onClick={e => { e.stopPropagation(); setFile(null); }}>
                  ✕
                </button>
              </div>
            ) : (
              <>
                <div style={{ fontSize: 20, marginBottom: 6 }}>🧾</div>
                <div style={{ fontSize: 13, fontWeight: 600, 
                  color: "var(--wd-t2)" }}>
                  Drop GST tax invoice PDF or click to browse
                </div>
              </>
            )}
          </div>

          <div style={{ maxWidth: 200 }}>
            <StepLabel>Invoice Amount (₹)</StepLabel>
            <input className="wd-input" type="number" placeholder="e.g. 92000"
              value={amount} onChange={e => setAmount(e.target.value)} />
          </div>

          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button className="wd-btn wd-btn-ghost wd-btn-sm" 
              onClick={() => { setOpen(false); setFile(null); }} 
              disabled={saving}>
              Cancel
            </button>
            <button className="wd-btn wd-btn-primary wd-btn-sm" 
              onClick={handleUpload} disabled={saving || !file}>
              {saving ? "Uploading…" : "Upload Tax Invoice"}
            </button>
          </div>
        </div>
      )}
    </StepCard>
  );
}  

function PaymentRow({ icon, label, amount, statusLabel, statusColor, statusBg, statusBorder }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 12,
      padding: "10px 12px", borderRadius: 8,
      background: "var(--wd-hover)", border: "1px solid var(--wd-border)",
    }}>
      <span style={{ fontSize: 16, flexShrink: 0 }}>{icon}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 10, fontWeight: 700, textTransform: "uppercase",
          letterSpacing: ".05em", color: "var(--wd-t3)", marginBottom: 2,
        }}>
          {label}
        </div>
        <div style={{
          fontFamily: "var(--wd-mono)", fontSize: 13,
          fontWeight: 700, color: "var(--wd-t1)",
        }}>
          {amount != null ? formatINR(amount) : "—"}
        </div>
      </div>
      <span style={{
        fontSize: 10, fontWeight: 700, padding: "2px 9px", borderRadius: 20, flexShrink: 0,
        background: statusBg, color: statusColor, border: `1px solid ${statusBorder}`,
        textTransform: "uppercase", letterSpacing: ".04em", whiteSpace: "nowrap",
      }}>
        {statusLabel}
      </span>
    </div>
  );
}

export function StepPaymentsSettlement({ caseInfo, readOnly = false }) {
    const updateCaseStatus = useUpdateCaseStatus();
    // const autoAdvanced = useRef(false);
  //  useEffect(() => {
  //   if (caseInfo.currentStatus === "TAX_INVOICE_RECEIVED"  && !autoAdvanced.current) {
  //     autoAdvanced.current = true;
  //     updateCaseStatus.mutateAsync({
  //       caseId: caseInfo.accidentCaseNo,
  //       status: "PAYMENT_REQUEST_SENT",
  //       description: "Payment process initiated after tax invoice received",
  //     }).catch(console.error);
  //   }
  // }, [caseInfo.currentStatus, caseInfo.accidentCaseNo]);

  const navigate = useNavigate();

  // ── Derive payment states from caseInfo ──────────────────────────────────
  const customerDue  = caseInfo?.customerPayableAmount ?? null;
  const customerPaid = caseInfo?.customerPaymentStatus === "PAID";
 
  const insAmount    = caseInfo?.insuranceApprovedAmount || null;
  const claimStatus  = caseInfo?.claimStatus || "";
  const insReceived  = ["PAYMENT_RECEIVED", "SETTLED", "CASE_CLOSED"].includes(claimStatus);
  const insRequested = claimStatus === "PAYMENT_REQUESTED";
 
  const workshopAmount = caseInfo?.workshopEstimateAmount ?? null;
  const workshopPaid   = caseInfo?.workshopPaymentStatus === "PAID";
 
  const allDone = customerPaid && insReceived && workshopPaid;
 
  const pendingActions = [
    !insReceived  && (insRequested
      ? "Insurance payment requested — confirm once received"
      : "Insurance payment not yet requested"),
    !customerPaid
      ? "Customer payment not yet collected"
      : "Customer payment status unknown",
    !workshopPaid && "Workshop has not been paid yet",
  ].filter(Boolean);
 
  // ── Navigate to ClaimsPage, router state auto-opens the right modal ──────
  const goToClaims = (openModal) => {
    navigate("/admin/claims", {
      state: {
        highlightCase: caseInfo.accidentCaseNo,
        autoOpenModal: openModal,
        returnTo:      `/admin/cases/${caseInfo.accidentCaseNo}`,
        returnTab:     "Workshop",
      },
    });
  };
 
  // ── Primary CTA: most urgent action first ────────────────────────────────
  const primaryCta =
    !insReceived && insRequested
      ? { label: "✓ Mark Insurance Payment Received", modal: "payRecv",
          color: "var(--wd-green)",  bg: "rgba(34,197,94,.1)",  border: "rgba(34,197,94,.3)"  }
    : !insReceived
      ? { label: "📋 Update Claim Status",            modal: "status",
          color: "var(--wd-blue)",   bg: "rgba(59,130,246,.1)", border: "rgba(59,130,246,.3)" }
    // : !customerPaid && customerDue > 0
    //   ? { label: "📧 Notify Customer for Payment",    modal: "notify",
    //       color: "var(--wd-orange)", bg: "rgba(249,115,22,.1)", border: "rgba(249,115,22,.3)" }
    // : !workshopPaid
    //   ? { label: "🏭 Pay Workshop",                   modal: "payWorkshop",
    //       color: "var(--wd-yellow)", bg: "rgba(234,179,8,.1)",  border: "rgba(234,179,8,.3)"  }
    // : insReceived && !allDone
    //   ? { label: "✓ Settle Claim",                    modal: "settle",
    //       color: "var(--wd-green)",  bg: "rgba(34,197,94,.1)",  border: "rgba(34,197,94,.3)"  }
    : null;
 
  // ── Status chip configs ──────────────────────────────────────────────────
  const customerChip = (customerPaid || customerDue === 0)
    ? { statusLabel: customerDue === 0 ? "✓ NIL" : "✓ Paid",
        statusColor: "var(--wd-green)",  statusBg: "rgba(34,197,94,.12)",  statusBorder: "rgba(34,197,94,.25)"  }
    : { statusLabel: "✗ Pending",
        statusColor: "var(--wd-red)",    statusBg: "rgba(239,68,68,.10)",  statusBorder: "rgba(239,68,68,.20)"  };
 
  const insChip = insReceived
    ? { statusLabel: "✓ Received",
        statusColor: "var(--wd-green)",  statusBg: "rgba(34,197,94,.12)",  statusBorder: "rgba(34,197,94,.25)"  }
    : insRequested
    ? { statusLabel: "⏳ Requested",
        statusColor: "var(--wd-yellow)", statusBg: "rgba(234,179,8,.12)",  statusBorder: "rgba(234,179,8,.25)"  }
    : { statusLabel: "✗ Pending",
        statusColor: "var(--wd-red)",    statusBg: "rgba(239,68,68,.10)",  statusBorder: "rgba(239,68,68,.20)"  };
 
  const workshopChip = workshopPaid
    ? { statusLabel: "✓ Paid",
        statusColor: "var(--wd-green)", statusBg: "rgba(34,197,94,.12)", statusBorder: "rgba(34,197,94,.25)" }
    : { statusLabel: "✗ Pending",
        statusColor: "var(--wd-red)",   statusBg: "rgba(239,68,68,.10)", statusBorder: "rgba(239,68,68,.20)" };
 
  const state = allDone ? "done" : pendingActions.length === 3 ? "active" : "warning";
 
  return (
    <StepCard state={state}>
      <StepHeader
        icon={allDone ? "✅" : "💰"}
        title="Payments & settlement"
        subtitle={
          allDone
            ? "All payments complete — ready to close the case"
            : `${pendingActions.length} payment${pendingActions.length !== 1 ? "s" : ""} pending`
        }
        badge={allDone ? "Complete" : "Action Required"}
        badgeColor={allDone ? "var(--wd-green)" : "var(--wd-orange)"}
      />
 
      {/* ── Payment summary ── */}
      <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 6 }}>
        <StepLabel>Payment summary</StepLabel>
        <PaymentRow
          icon="🏦" label="Insurance payment" amount={insAmount}
          {...insChip}
        />
        <PaymentRow
          icon="👤" label="Customer payment" amount={customerDue}
          {...customerChip}
        />
        <PaymentRow
          icon="🔧" label="Workshop payment" amount={workshopAmount}
          {...workshopChip}
        />
      </div>
 
      {/* ── Pending actions list ── */}
      {!allDone && pendingActions.length > 0 && (
        <div style={{
          marginTop: 12, padding: "10px 14px", borderRadius: 9,
          background: "rgba(249,115,22,.06)", border: "1px solid rgba(249,115,22,.18)",
        }}>
          <div style={{
            fontSize: 10, fontWeight: 700, textTransform: "uppercase",
            letterSpacing: ".05em", color: "var(--wd-orange)", marginBottom: 6,
          }}>
            ⚠ Pending actions
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            {pendingActions.map((action, i) => (
              <div key={i} style={{
                display: "flex", alignItems: "flex-start", gap: 7,
                fontSize: 12, color: "var(--wd-t2)",
              }}>
                <span style={{ color: "var(--wd-orange)", flexShrink: 0, marginTop: 1 }}>•</span>
                {action}
              </div>
            ))}
          </div>
        </div>
      )}
 
      {/* ── All done banner ── */}
      {allDone && (
        <div style={{
          marginTop: 12, padding: "10px 14px", borderRadius: 9,
          background: "rgba(34,197,94,.07)", border: "1px solid rgba(34,197,94,.2)",
          display: "flex", alignItems: "center", gap: 10,
        }}>
          <span style={{ fontSize: 14 }}>🎉</span>
          <div style={{ fontSize: 12, color: "var(--wd-green)", fontWeight: 600 }}>
            All payments complete — close the case from the Claims page.
          </div>
        </div>
      )}
 
      {/* ── Action buttons ── */}
      {!readOnly && !allDone &&(
        <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 7 }}>
 
          {/* Primary — opens the exact right modal in ClaimsPage */}
          {primaryCta && (
            <button
              onClick={() => goToClaims(primaryCta.modal)}
              style={{
                width: "100%", padding: "9px 14px", borderRadius: 8,
                border: `1px solid ${primaryCta.border}`,
                background: primaryCta.bg, color: primaryCta.color,
                fontSize: 12, fontWeight: 700, cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
              }}
              onMouseEnter={e => e.currentTarget.style.opacity = ".82"}
              onMouseLeave={e => e.currentTarget.style.opacity = "1"}
            >
              {primaryCta.label}
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2.5">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </button>
          )}

           {/* Customer + Workshop side by side when both pending */}
    {!customerPaid  || (insReceived && !workshopPaid) ? (
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 7 }}>

        {!customerPaid  && (
          <button
            onClick={() => goToClaims("custPay")}
            style={{
              padding: "8px 10px", borderRadius: 8,
              border: "1px solid rgba(249,115,22,.3)",
              background: "rgba(249,115,22,.08)", color: "var(--wd-orange)",
              fontSize: 11, fontWeight: 700, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
            }}
            onMouseEnter={e => e.currentTarget.style.opacity = ".82"}
            onMouseLeave={e => e.currentTarget.style.opacity = "1"}
          >
            { "💵 Collect from Customer"}
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </button>
        )}

        {insReceived && !workshopPaid && (
          <button
            onClick={() => goToClaims("payWorkshop")}
            style={{
              padding: "8px 10px", borderRadius: 8,
              border: "1px solid rgba(234,179,8,.3)",
              background: "rgba(234,179,8,.08)", color: "var(--wd-yellow)",
              fontSize: 11, fontWeight: 700, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
            }}
            onMouseEnter={e => e.currentTarget.style.opacity = ".82"}
            onMouseLeave={e => e.currentTarget.style.opacity = "1"}
          >
            🏭 Pay Workshop
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </button>
        )}

        {/* Fill empty cell if only one button shows */}
        {(!customerPaid && customerDue > 0) && !(insReceived && !workshopPaid) && <div />}
        {!((!customerPaid && customerDue > 0)) && (insReceived && !workshopPaid) && <div />}

      </div>
    ) : null}

          
 
          {/* Secondary — open ClaimsPage without auto-opening a modal */}
          <button
            onClick={() => goToClaims(null)}
            style={{
              width: "100%", padding: "7px 14px", borderRadius: 8,
              border: "1px solid var(--wd-border)",
              background: "transparent", color: "var(--wd-t3)",
              fontSize: 11, fontWeight: 600, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
            }}
          >
            Open Claims page →
          </button>
        </div>
      )}
    </StepCard>
  );
}

export function StepMarkPaymentsReceived({ caseInfo, onRefetch }) {
  const updateCaseStatus = useUpdateCaseStatus();

  const done = ["PAYMENT_COMPLETED", "CLAIM_SETTLED", "CASE_CLOSED"]
    .includes(caseInfo.currentStatus);

  const [open,   setOpen]   = useState(false);
  const [date,   setDate]   = useState(new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      await updateCaseStatus.mutateAsync({
        caseId:      caseInfo.accidentCaseNo,
        status:      "PAYMENT_COMPLETED",
        description: `All payments received on ${date}`,
        statusDate:  date,
      });
      onRefetch?.({ currentStatus: "PAYMENT_COMPLETED" });
      setOpen(false);
    } catch (e) { console.error(e); }
    setSaving(false);
  }, [caseInfo.accidentCaseNo, date, updateCaseStatus, onRefetch]);

  if (done) {
    return (
      <StepCard state="done">
        <StepHeader
          icon="✅"
          title="Payments Received"
          subtitle="All payments have been marked as received"
          badge="Done"
          badgeColor="var(--wd-green)"
        />
      </StepCard>
    );
  }

  return (
    <StepCard state="active">
      <StepHeader
        icon="💳"
        title="Mark Payments Received"
        subtitle="Confirm all payments (insurance, customer, workshop) have been received"
        badge="Action Required"
        badgeColor="var(--wd-blue)"
      />

      {!open && (
        <div style={{ marginTop: 10 }}>
          <button className="wd-btn wd-btn-primary wd-btn-sm" onClick={() => setOpen(true)}>
            ✓ Mark All Payments Received
          </button>
        </div>
      )}

      {open && (
        <div style={{
          marginTop: 12, paddingTop: 12,
          borderTop: "1px dashed var(--wd-border)",
          display: "flex", flexDirection: "column", gap: 10,
        }}>
          <div>
            <StepLabel>Date *</StepLabel>
            <input
              className="wd-input" type="date" value={date}
              max={new Date().toISOString().slice(0, 10)}
              onChange={e => setDate(e.target.value)}
              style={{ maxWidth: 200 }}
            />
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button className="wd-btn wd-btn-ghost wd-btn-sm"
              onClick={() => setOpen(false)} disabled={saving}>Cancel</button>
            <button className="wd-btn wd-btn-primary wd-btn-sm"
              onClick={handleSave} disabled={saving || !date}>
              {saving ? "Saving…" : "✓ Confirm"}
            </button>
          </div>
        </div>
      )}
    </StepCard>
  );
}

export function StepSettleClaim({ caseInfo, onRefetch }) {
  const navigate = useNavigate();
 
  const done     = ["CLAIM_SETTLED", "CASE_CLOSED"].includes(caseInfo.currentStatus);
  const unlocked = ["PAYMENT_COMPLETED", "CLAIM_SETTLED", "CASE_CLOSED"]
    .includes(caseInfo.currentStatus);
 
  const goToClaims = (modal) => {
    navigate("/admin/claims", {
      state: {
        highlightCase: caseInfo.accidentCaseNo,
        autoOpenModal: modal,
        returnTo:      `/admin/cases/${caseInfo.accidentCaseNo}`,
        returnTab:     "Workshop",
      },
    });
  };
 
  if (done) {
    return (
      <StepCard state="done">
        <StepHeader
          icon="✅"
          title="Claim settled"
          subtitle={caseInfo.settledAt ? `Settled on ${formatDate(caseInfo.settledAt)}` : "Claim has been marked as settled"}
          badge="Settled"
          badgeColor="var(--wd-green)"
        />
      </StepCard>
    );
  }
 
  // Locked — waiting for payments to be marked received
  if (!unlocked) {
    return (
      <StepCard state="pending">
        <StepHeader
          icon="🔒"
          title="Settle claim"
          subtitle="Unlocks after insurance payment is marked received"
          badge="Locked"
          badgeColor="var(--wd-t3)"
        />
      </StepCard>
    );
  }
 
  return (
    <StepCard state="warning">
      <StepHeader
        icon="🤝"
        title="Settle claim"
        subtitle="Payment received — mark the claim as officially settled in the Claims page"
        badge="Action Required"
        badgeColor="var(--wd-orange)"
      />
 
      <div style={{
        marginTop: 12, padding: "10px 14px", borderRadius: 10,
        background: "rgba(249,115,22,.06)", border: "1px solid rgba(249,115,22,.18)",
        fontSize: 12, color: "var(--wd-t2)", lineHeight: 1.6,
      }}>
        ℹ️ This will record the final settlement amount and mark the claim as{" "}
        <strong style={{ color: "var(--wd-t1)" }}>Settled</strong>. After settling, you can close the case.
      </div>
 
      <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 7 }}>
        <button
          onClick={() => goToClaims("settle")}
          style={{
            width: "100%", padding: "9px 14px", borderRadius: 8,
            border: "1px solid rgba(249,115,22,.3)",
            background: "rgba(249,115,22,.1)", color: "var(--wd-orange)",
            fontSize: 12, fontWeight: 700, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
          }}
          onMouseEnter={e => e.currentTarget.style.opacity = ".82"}
          onMouseLeave={e => e.currentTarget.style.opacity = "1"}
        >
          ✓ Settle Claim
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M5 12h14M12 5l7 7-7 7"/>
          </svg>
        </button>
 
        <button
          onClick={() => goToClaims(null)}
          style={{
            width: "100%", padding: "7px 14px", borderRadius: 8,
            border: "1px solid var(--wd-border)",
            background: "transparent", color: "var(--wd-t3)",
            fontSize: 11, fontWeight: 600, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
          }}
        >
          Open Claims page →
        </button>
      </div>
    </StepCard>
  );
}
 
// ─────────────────────────────────────────────────────────────────────────────
// StepCloseCase
// Opens the "Close Case" modal in ClaimsPage (closeCase)
// ─────────────────────────────────────────────────────────────────────────────
export function StepCloseCase({ caseInfo, onRefetch }) {
  const navigate = useNavigate();
 
  const done     = caseInfo.currentStatus === "CASE_CLOSED";
  const unlocked = ["CLAIM_SETTLED", "CASE_CLOSED"].includes(caseInfo.currentStatus);
 
  const goToClaims = (modal) => {
    navigate("/admin/claims", {
      state: {
        highlightCase: caseInfo.accidentCaseNo,
        autoOpenModal: modal,
        returnTo:      `/admin/cases/${caseInfo.accidentCaseNo}`,
        returnTab:     "Workshop",
      },
    });
  };
 
  if (done) {
    return (
      <StepCard state="done">
        <StepHeader
          icon="✅"
          title="Case closed"
          subtitle={caseInfo.closedAt ? `Closed on ${formatDate(caseInfo.closedAt)}` : "Case has been closed"}
          badge="Closed"
          badgeColor="var(--wd-green)"
        />
        <div style={{
          marginTop: 12, padding: "12px 14px", borderRadius: 10,
          background: "rgba(34,197,94,.07)", border: "1px solid rgba(34,197,94,.2)",
          display: "flex", alignItems: "center", gap: 10,
        }}>
          <span style={{ fontSize: 22 }}>🎉</span>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "var(--wd-green)" }}>Workflow complete</div>
            <div style={{ fontSize: 11, color: "var(--wd-t3)", marginTop: 2 }}>
              Case ID: <span style={{ fontFamily: "var(--wd-mono)" }}>{caseInfo.accidentCaseNo}</span>
            </div>
          </div>
        </div>
      </StepCard>
    );
  }
 
  // Locked — waiting for claim to be settled
  if (!unlocked) {
    return (
      <StepCard state="pending">
        <StepHeader
          icon="🔒"
          title="Close case"
          subtitle="Unlocks after claim is marked settled"
          badge="Locked"
          badgeColor="var(--wd-t3)"
        />
      </StepCard>
    );
  }
 
  return (
    <StepCard state="active">
      <StepHeader
        icon="🔒"
        title="Close case"
        subtitle="Claim settled — close the case to complete the full workflow"
        badge="Action Required"
        badgeColor="var(--wd-blue)"
      />
 
      <div style={{
        marginTop: 12, padding: "10px 14px", borderRadius: 10,
        background: "rgba(168,85,247,.06)", border: "1px solid rgba(168,85,247,.2)",
        fontSize: 12, color: "var(--wd-t2)", lineHeight: 1.6,
      }}>
        ℹ️ This is a <strong style={{ color: "var(--wd-purple, #a855f7)" }}>final action</strong>. Once closed, no further updates can be made. Ensure all payments and paperwork are complete.
      </div>
 
      <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 7 }}>
        <button
          onClick={() => goToClaims("closeCase")}
          style={{
            width: "100%", padding: "9px 14px", borderRadius: 8,
            border: "1px solid rgba(168,85,247,.3)",
            background: "rgba(168,85,247,.1)", color: "var(--wd-purple, #a855f7)",
            fontSize: 12, fontWeight: 700, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
          }}
          onMouseEnter={e => e.currentTarget.style.opacity = ".82"}
          onMouseLeave={e => e.currentTarget.style.opacity = "1"}
        >
          🔒 Close Case
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M5 12h14M12 5l7 7-7 7"/>
          </svg>
        </button>
 
        <button
          onClick={() => goToClaims(null)}
          style={{
            width: "100%", padding: "7px 14px", borderRadius: 8,
            border: "1px solid var(--wd-border)",
            background: "transparent", color: "var(--wd-t3)",
            fontSize: 11, fontWeight: 600, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
          }}
        >
          Open Claims page →
        </button>
      </div>
    </StepCard>
  );
}
 














// ── Extracted upload form to keep StepProforma readable ──────────────────────
function UploadForm({ file, setFile, amount, setAmount, dragOver, setDragOver, saving, onSave, onCancel }) {
  return (
    <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 12 }}>
      <div
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={e => {
          e.preventDefault(); setDragOver(false);
          const f = e.dataTransfer.files[0];
          if (f?.type === "application/pdf") setFile(f);
        }}
        style={{
          border: `2px dashed ${dragOver ? "var(--wd-blue)" : file ? "var(--wd-green)" : "var(--wd-border-md)"}`,
          borderRadius: 10, padding: "18px 16px",
          background: dragOver ? "rgba(59,130,246,.05)" : file ? "rgba(34,197,94,.05)" : "var(--wd-hover)",
          textAlign: "center", cursor: "pointer", transition: "all .15s",
        }}
        onClick={() => document.getElementById("proforma-upload-input").click()}
      >
        <input id="proforma-upload-input" type="file" accept="application/pdf"
          style={{ display: "none" }}
          onChange={e => { const f = e.target.files[0]; if (f?.type === "application/pdf") setFile(f); }} />
        {file ? (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
            <span style={{ fontSize: 20 }}>📄</span>
            <div style={{ textAlign: "left" }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--wd-green)" }}>{file.name}</div>
              <div style={{ fontSize: 11, color: "var(--wd-t3)" }}>{(file.size/1024).toFixed(0)} KB</div>
            </div>
            <button className="wd-btn wd-btn-ghost wd-btn-sm" style={{ fontSize: 11 }}
              onClick={e => { e.stopPropagation(); setFile(null); }}>✕</button>
          </div>
        ) : (
          <>
            <div style={{ fontSize: 20, marginBottom: 6 }}>📤</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: "var(--wd-t2)" }}>Drop PDF or click to browse</div>
          </>
        )}
      </div>
      <div style={{ maxWidth: 200 }}>
        <StepLabel>Proforma Amount (₹)</StepLabel>
        <input className="wd-input" type="number" placeholder="e.g. 92000"
          value={amount} onChange={e => setAmount(e.target.value)} />
      </div>
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <button className="wd-btn wd-btn-ghost wd-btn-sm" onClick={onCancel} disabled={saving}>Cancel</button>
        <button className="wd-btn wd-btn-primary wd-btn-sm" onClick={onSave} disabled={saving || !file}>
          {saving ? "Uploading…" : "Upload Proforma"}
        </button>
      </div>
    </div>
  );
}
// Repair progress timeline (left sidebar)
// ─────────────────────────────────────────────────────────────────────────────
export function RepairTimeline({ caseInfo }) {
  const steps = [
    { label: "Workshop Assigned",      done: !!caseInfo.workshopAssignedDate       },
    { label: "Vehicle Picked Up",      done: !!caseInfo.driverSentDate             },
    { label: "Vehicle at Workshop",    done: !!caseInfo.vehicleEntryDate           },
    { label: "Dismantling Approved",   done: !!caseInfo.dismantlingApprovedDate    },
    { label: "Dismantling Completed",  done: !!caseInfo.dismantlingCompletedDate   },
    { label: "2nd Visit Assigned",     done: !!caseInfo.workshopVisit2Date         },
    { label: "2nd Survey Done",        done: !!caseInfo.workshopSurveyCompletedDate},
    { label: "Final Approval",         done: !!caseInfo.finalApprovalDate          },
    { label: "Work Started",           done: !!caseInfo.workStartedDate            },
    { label: "Truck Repaired",         done: !!caseInfo.truckFinalizedDate         },
    { label: "Reinspection Done",      done: !!caseInfo.readyForInspectionDate     },
    { label: "Proforma Uploaded",      done: !!caseInfo.proformaDoc                },
    { label: "Proforma Approved",      done: !!caseInfo.proformaApprovedDate       },
    { label: "Assessment Received",     done: !!caseInfo.assessmentReceivedDate     },
    { label: "Assessment Cross-Checked", done: !!caseInfo.assessmentCrossCheckedDate },
    { label: "Tax Invoice Received",   done: !!caseInfo.taxInvoiceDoc              },
  ];
  const completedCount = steps.filter(s => s.done).length;
 
  return (
    <SectionCard title={`Repair Progress (${completedCount}/${steps.length})`}>
      <div style={{ display: "flex", flexDirection: "column" }}>
        {steps.map((step, i) => (
          <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
              <div style={{
                width: 18, height: 18, borderRadius: "50%", marginTop: 1,
                background: step.done ? "var(--wd-green)" : "var(--wd-hover)",
                border: `2px solid ${step.done ? "var(--wd-green)" : "var(--wd-border-md)"}`,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                {step.done && (
                  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                )}
              </div>
              {i < steps.length - 1 && (
                <div style={{ width: 2, height: (step.done && step.date) ? 26 : 16, background: step.done ? "var(--wd-green)" : "var(--wd-border)" }} />
              )}
            </div>
            <div style={{ paddingBottom: 2 }}>
              <span style={{ fontSize: 12, color: step.done ? "var(--wd-t1)" : "var(--wd-t3)", fontWeight: step.done ? 500 : 400 }}>
                {step.label}
              </span>
            </div>
          </div>
        ))}
      </div>
    </SectionCard>
  );
}

